/**
 * Permission Checker Utilities
 * Extracted from authService.ts
 * Handles permission validation logic
 */

import type { User } from "../services/authService";
import { PermissionService } from "../services/PermissionService";

/**
 * Role hierarchy for project permissions
 * owner > editor > viewer
 */
const ROLE_HIERARCHY = {
  owner: 3,
  editor: 2,
  viewer: 1,
} as const;

type ProjectRole = keyof typeof ROLE_HIERARCHY;

/**
 * Check if user has a specific permission
 * Admin users bypass all permission checks
 */
export function hasPermission(
  user: User | null,
  resource: string,
  action: string,
  view: string | null = null
): boolean {
  // AuthService and AuthContext must enforce the same explicit-deny and
  // project-view policy instead of carrying diverging fallback implementations.
  return new PermissionService(user).hasPermission(resource, action, view);
}

/**
 * Check if user has project-level permissions
 * Uses role hierarchy: owner > editor > viewer
 */
export function hasProjectPermission(
  user: User | null,
  projectId: string,
  requiredRole: ProjectRole
): boolean {
  if (!user) return false;

  // Admin bypass
  if (user.role === "admin") return true;

  // Check project access from JWT
  if (!user.projects) return false;

  const projectAccess = user.projects.find((p) => (p.project_id ?? p.id) === projectId);
  if (!projectAccess) return false;

  // Role hierarchy: owner > editor > viewer
  const userRoleLevel = ROLE_HIERARCHY[projectAccess.role as ProjectRole] || 0;
  const requiredRoleLevel = ROLE_HIERARCHY[requiredRole] || 0;

  return userRoleLevel >= requiredRoleLevel;
}

/**
 * Check if user is admin
 */
export function isAdmin(user: User | null): boolean {
  return user?.role === "admin";
}
